
import { Avatar } from "./ui/avatar";
import { Button } from "./ui/button";
import { ReportDialog } from "./ReportDialog";
import { useState, useMemo } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import { formatDistanceToNow } from "date-fns";
import { CommentReactions } from "./CommentReactions";
import { CommentForm } from "./CommentForm";

interface Comment {
  id?: string;
  username: string;
  userId: string;
  content: string;
  createdAt?: string;
  parentId?: string;
  replies?: Comment[];
  reactions?: {
    likes: number;
    hearts: number;
    supports: number;
  };
  postId: string;
}

interface CommentThreadProps {
  comment: Comment;
  onAddComment: (content: string, parentId?: string) => void;
  onAddReaction: (commentId: string, reactionType: 'likes' | 'hearts' | 'supports') => void;
  level?: number;
}

export const CommentThread = ({ 
  comment, 
  onAddComment, 
  onAddReaction,
  level = 0 
}: CommentThreadProps) => {
  const [isReplying, setIsReplying] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Deduplicate replies
  const uniqueReplies = useMemo(() => {
    if (!comment.replies || comment.replies.length === 0) return [];
    
    const repliesMap = new Map();
    comment.replies.forEach(reply => {
      if (reply.id && !repliesMap.has(reply.id)) {
        repliesMap.set(reply.id, reply);
      }
    });
    
    return Array.from(repliesMap.values());
  }, [comment.replies]);

  const hasReplies = uniqueReplies.length > 0;

  return (
    <div className="space-y-2" style={{ marginLeft: `${level * 24}px` }}>
      <Collapsible open={!isCollapsed} onOpenChange={setIsCollapsed}>
        <div className="flex items-start gap-2">
          <Avatar className="w-8 h-8" />
          <div className="flex-1 bg-accent rounded-2xl p-3">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-sm">{comment.username}</span>
              {comment.createdAt && (
                <span className="text-xs text-muted-foreground">
                  {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
                </span>
              )}
            </div>
            <p className="text-sm mt-1">{comment.content}</p>
            
            <div className="flex items-center gap-4 mt-2">
              <CommentReactions
                reactions={comment.reactions || { likes: 0, hearts: 0, supports: 0 }}
                commentId={comment.id || ''}
                onAddReaction={(type) => onAddReaction(comment.id || '', type)}
              />
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setIsReplying(!isReplying)}
                className="text-sm"
              >
                Reply
              </Button>
            </div>
          </div>
          <ReportDialog
            contentType="comment"
            contentId={comment.id || ""}
            reportedUserId={comment.userId}
            onReport={(reason) => console.log("Comment reported:", { reason, commentId: comment.id })}
          />
          {hasReplies && (
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                {isCollapsed ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronUp className="h-4 w-4" />
                )}
              </Button>
            </CollapsibleTrigger>
          )}
        </div>

        {isReplying && (
          <div className="mt-2" style={{ marginLeft: '32px' }}>
            <CommentForm
              onSubmit={(content) => {
                onAddComment(content, comment.id);
                setIsReplying(false);
              }}
              placeholder="Write a reply..."
              buttonText="Reply"
              postId={comment.postId}
            />
          </div>
        )}

        {hasReplies && (
          <CollapsibleContent className="mt-2">
            {uniqueReplies.map((reply) => (
              <CommentThread
                key={reply.id}
                comment={reply}
                onAddComment={onAddComment}
                onAddReaction={onAddReaction}
                level={level + 1}
              />
            ))}
          </CollapsibleContent>
        )}
      </Collapsible>
    </div>
  );
};
